// Central date/time formatting. The backend stores and sends timestamps in UTC, but Java
// LocalDateTime serializes WITHOUT a timezone designator (e.g. "2026-07-21T23:29:36"). JavaScript
// parses such zone-less date-time strings as *browser-local* time, which is wrong. So we parse
// backend strings as UTC and then render everything in IST (Asia/Kolkata), independent of the
// viewer's browser timezone.

const IST = 'Asia/Kolkata';
const LOCALE = 'en-IN';

// A trailing "Z" or a "+05:30"/"-0800" style offset means the string already carries a zone.
const HAS_TZ = /([zZ])$|[+-]\d{2}:?\d{2}$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a value coming from the backend into a Date, treating zone-less date-time strings as UTC.
 * Accepts Date, epoch number, ISO string (with or without zone), or "YYYY-MM-DD HH:mm:ss".
 * Returns null for empty/invalid input.
 */
export function parseBackendDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== 'string') return null;

  let s = value.trim();
  if (!s) return null;

  if (!HAS_TZ.test(s)) {
    // Normalize a space-separated timestamp to ISO, then mark it UTC (unless it's a bare date,
    // which new Date() already interprets as UTC midnight).
    if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
    if (!DATE_ONLY.test(s)) s += 'Z';
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format with arbitrary Intl options, always in IST. Returns `fallback` for empty/invalid input. */
export function formatInIST(value, options = {}, fallback = '—') {
  const d = parseBackendDate(value);
  if (!d) return fallback;
  return d.toLocaleString(LOCALE, { timeZone: IST, ...options });
}

/** Date + time in IST, e.g. "21 Jul 2026, 11:29 pm". */
export function formatDateTime(value, fallback = '—') {
  return formatInIST(
    value,
    { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
    fallback
  );
}

/** Date only in IST, e.g. "21 Jul 2026". */
export function formatDate(value, fallback = '—') {
  return formatInIST(value, { day: '2-digit', month: 'short', year: 'numeric' }, fallback);
}

/** Time only in IST, e.g. "11:29 pm". */
export function formatTime(value, fallback = '—') {
  return formatInIST(value, { hour: '2-digit', minute: '2-digit' }, fallback);
}

/** Backend (UTC) timestamp -> "YYYY-MM-DDTHH:mm" IST value for a datetime-local input. */
export function toIstInputValue(value) {
  const d = parseBackendDate(value);
  if (!d) return '';
  return new Date(d.getTime() + 330 * 60 * 1000).toISOString().slice(0, 16);
}

/** IST datetime-local value -> zone-less UTC string the backend stores. */
export function istInputToBackend(value) {
  if (!value) return null;
  const d = new Date(`${value}:00+05:30`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 19);
}
